import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Divider, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { auth } from "../../config/firebaseConfig";

const NotificationOverlay = ({ onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notifications?unread_only=false&limit=5`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_API_URL}/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Update local state
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <Box
      sx={{
        backgroundColor: "#e1ecc8",
        borderRadius: "0 0 20px 20px",
        paddingTop: "30px",
        maxWidth: "90vw",
        width: { xs: "280px", sm: "300px", md: "321px" },
        padding: { xs: "15px", sm: "20px" },
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#516d4f" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", fontFamily: "Poppins, sans-serif" }}>Notifications</Typography>
      </Box>

      {/* Notification Items */}
      <Box sx={{ maxHeight: "256px", overflowY: "auto", paddingRight: "5px" }}>
        {loading ? (
          // Skeleton loading
          Array.from({ length: 3 }).map((_, index) => (
            <Box key={index} sx={{ padding: "12px" }}>
              <Skeleton 
                variant="text" 
                width="60%" 
                height={24} 
                sx={{ backgroundColor: "rgba(160, 196, 157, 0.3)" }} 
              />
              <Skeleton 
                variant="text" 
                width="90%" 
                sx={{ backgroundColor: "rgba(160, 196, 157, 0.3)" }} 
              />
              <Divider sx={{ my: 1 }} />
            </Box>
          ))
        ) : notifications.length > 0 ? (
          notifications.map((notification) => (
            <Box 
              key={notification.id} 
              sx={{ 
                padding: "12px", 
                borderRadius: "12px", 
                transition: "background 0.3s", 
                cursor: "pointer",
                backgroundColor: notification.is_read ? 'transparent' : 'rgba(160, 196, 157, 0.15)',
                "&:hover": { backgroundColor: "#d1dfb5" } 
              }}
              onClick={() => {
                // Mark as read before navigating
                if (!notification.is_read) {
                  markAsRead(notification.id);
                }
                navigate('/gso/notification', { state: { highlightId: notification.id } });
                if (onClose) onClose();
              }}
            >
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: notification.is_read ? 600 : 700, 
                  fontFamily: "Poppins, sans-serif", 
                  color: notification.is_read ? "#707070" : "#516d4f" 
                }}
              >
                {notification.title}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: notification.is_read ? "#707070" : "#516d4f",
                  fontWeight: notification.is_read ? 400 : 500
                }}
              >
                {notification.message}
              </Typography>
              <Divider sx={{ my: 1 }} />
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ textAlign: "center", color: "#999" }}>
            No new notifications.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default NotificationOverlay;